import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { catchError, map } from 'rxjs';
import { IdentifiableEntities } from '../common/list-entity';
import {
  Monitor,
  MonitorAvailabilityStatistics,
  MonitorAvailabilityTimelineEntry,
  MonitorFailure,
  ProbeMetrics,
  RegionHealth,
  RegionStatistics,
} from 'src/app/models/monitor';
import { ToastService } from 'src/app/services/toast/toast.service';
import {
  deleteEntity,
  Entity,
  loadEntity,
  NewEntity,
} from 'src/app/store/common/entity';
import { UserState } from 'src/app/store/user/user.state';
import {
  ListEntities,
  loadEntities,
  NewListEntities,
} from '../common/list-entity';
import {
  PaginatedListEntities,
  PaginatedListEntitiesState,
  TransformPaginatedEntitiesToIterableEntities,
} from '../common/paginated-list-entity';
import { OffsetPagination, OffsetPaginationState } from '../common/pagination';
import {
  DeleteMonitorAction,
  GetMonitorAction,
  LoadMonitorFailuresAction,
  LoadRegionHealthAction,
  MonitorUpdatedAction,
} from './monitors.actions';
import { MonitorService } from './monitors.service';
export interface MonitorDetailsStateModel {
  monitor: Entity<Monitor>;
  regionHealth: ListEntities<RegionHealth>;
  monitorFailures: PaginatedListEntitiesState<
    MonitorFailure,
    OffsetPaginationState
  >;
  regions: ListEntities<RegionStatistics>;
  monitorProbeMetrics: Entity<ProbeMetrics>;
  globalAvailability: Entity<MonitorAvailabilityStatistics>;
  globalAvailabilityTimeline: MonitorAvailabilityTimelineEntry[];
}

@State<MonitorDetailsStateModel>({
  name: 'monitor',
  children: [],
  defaults: {
    monitor: NewEntity(),
    regionHealth: NewListEntities<RegionHealth>(),
    monitorFailures: OffsetPagination.GetDefaultState(),
    regions: NewListEntities<RegionStatistics>(),
    monitorProbeMetrics: NewEntity<ProbeMetrics>(),
    globalAvailability: NewEntity<MonitorAvailabilityStatistics>(),
    globalAvailabilityTimeline: [],
  },
})
@Injectable()
export class MonitorDetailsState {
  store = inject(Store);
  toast = inject(ToastService);
  monitorService = inject(MonitorService);

  monitorFailures = new PaginatedListEntities<
    MonitorDetailsStateModel,
    OffsetPagination
  >('monitorFailures', new OffsetPagination());

  @Selector()
  static monitor(state: MonitorDetailsStateModel) {
    return state.monitor;
  }

  @Selector()
  static regionHealth(state: MonitorDetailsStateModel) {
    return state.regionHealth;
  }

  @Selector()
  static monitorFailures(state: MonitorDetailsStateModel) {
    return TransformPaginatedEntitiesToIterableEntities(state.monitorFailures);
  }

  @Selector()
  static regions(state: MonitorDetailsStateModel) {
    return state.regions;
  }

  @Selector()
  static monitorProbeMetrics(state: MonitorDetailsStateModel) {
    return state.monitorProbeMetrics;
  }

  @Selector()
  static globalAvailability(state: MonitorDetailsStateModel) {
    return state.globalAvailability;
  }

  @Selector()
  static globalAvailabilityTimeline(state: MonitorDetailsStateModel) {
    return state.globalAvailabilityTimeline;
  }

  @Action(GetMonitorAction)
  getMonitor(
    ctx: StateContext<MonitorDetailsStateModel>,
    { monitorId }: GetMonitorAction
  ) {
    const organizationID = this.store.selectSnapshot(
      UserState.getCurrentOrganizationID
    );
    if (!organizationID) {
      throw new Error('No organizationID set');
    }

    return loadEntity(
      ctx,
      'monitor',
      this.monitorService.getMonitor(organizationID, monitorId),
      true
    ).pipe(
      catchError((err: any) => {
        this.toast.showError(err);
        throw err;
      })
    );
  }

  @Action(MonitorUpdatedAction)
  updateMonitor(
    ctx: StateContext<MonitorDetailsStateModel>,
    { monitor }: MonitorUpdatedAction
  ) {
    if (ctx.getState().monitor.data?.id === monitor.id) {
      ctx.setState({
        ...ctx.getState(),
        monitor: {
          ...ctx.getState().monitor,
          data: monitor,
        },
      });
    }
  }

  @Action(DeleteMonitorAction)
  deleteMonitor(
    ctx: StateContext<MonitorDetailsStateModel>,
    { monitorId }: DeleteMonitorAction
  ) {
    const organizationID = this.store.selectSnapshot(
      UserState.getCurrentOrganizationID
    );
    if (!organizationID) {
      throw new Error('No organizationID set');
    }

    return deleteEntity(
      ctx,
      'monitor',
      this.monitorService.deleteMonitor(organizationID, monitorId)
    ).pipe(
      catchError((err: any) => {
        this.toast.showError(err);
        throw err;
      })
    );
  }

  @Action(LoadRegionHealthAction)
  loadRegionHealth(
    ctx: StateContext<MonitorDetailsStateModel>,
    { monitorId, refresh }: LoadRegionHealthAction
  ) {
    const organizationID = this.store.selectSnapshot(
      UserState.getCurrentOrganizationID
    );
    if (!organizationID) {
      throw new Error('No organizationID set');
    }

    // Non-refresh loads happen on monitor navigation — clear so consumers
    // never see the previous monitor's region health while the new one is
    // in flight. Refresh polls keep the current data visible instead.
    return loadEntities(
      ctx,
      'regionHealth',
      this.monitorService.loadRegionHealth(organizationID, monitorId).pipe(
        map(items =>
          items.reduce<IdentifiableEntities<RegionHealth>>((acc, item) => {
            acc[item.region.slug] = { data: item };
            return acc;
          }, {})
        )
      ),
      { clear: !refresh }
    );
  }

  @Action(LoadMonitorFailuresAction)
  loadMonitorFailures(
    ctx: StateContext<MonitorDetailsStateModel>,
    { monitorId, options, refresh }: LoadMonitorFailuresAction
  ) {
    const organizationID = this.store.selectSnapshot(
      UserState.getCurrentOrganizationID
    );
    if (!organizationID) {
      throw new Error('No organizationID set');
    }

    return this.monitorFailures.loadEntititesPaginated(
      ctx,
      options.pagination,
      this.monitorService.loadMonitorFailures(
        organizationID,
        monitorId,
        options
      ),
      false,
      refresh
    );
  }
}
